import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi/useApi.ts";
import { useNotification } from "~hooks/useNotification.ts";
import type { SearchFilterParams } from "~pages/Conducts/ConductListPage/types/searchFilterParams.ts";
import type { UpsertConductData } from "~pages/Conducts/ConductUpsertModal/types/upsertConductData.ts";
import type { Conduct, ConductResponse } from "~types/conduct.ts";

export const useUpsertConduct = (params: SearchFilterParams) => {
  const { sport, organizationId } = useParams();
  const request = useApi();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation<ConductResponse, Error, UpsertConductData>({
    mutationFn: async (upsertModel: UpsertConductData) => {
      let url = `${sport}/o/${organizationId}/conduct`;
      if (upsertModel.conductId) {
        url = `${sport}/o/${organizationId}/conduct/${upsertModel.conductId}`;
      }
      const method = upsertModel.conductId ? "PUT" : "POST";
      const body = upsertModel.upsertData;
      return await request(url, { method, body });
    },
    onSuccess: async (response: { data: Array<Conduct> }) => {
      if (response?.data && response.data.length > 0) {
        await queryClient.invalidateQueries({
          queryKey: ["conducts", sport, organizationId, params],
          refetchType: "active",
        });

        notification.success({
          message: formatMessage({ id: "conduct.create.success" }),
          placement: "bottomRight",
        });
      }
    },
  });
};
